/**
 * Pure logic of the account profile dialog (capability `frontend-account-profile`): what the
 * owner may send, which passwords and files are acceptable, and how a cropped Blob becomes the
 * data URI the API stores. No Angular, no injection: unit-testable on its own.
 */
import type { HlmPasswordStrengthRules } from '@aurora';

// ─── Account → form values ──────────────────────────────────────────────────

/** The part of the session account the dialog reads; the generated `IamAccount` satisfies it. */
export interface ProfileAccountSource {
  id?: string | null;
  email?: string | null;
  username?: string | null;
  user?: {
    id?: string | null;
    name?: string | null;
    surname?: string | null;
    mobile?: string | null;
    langId?: string | null;
  } | null;
}

/** What the Account section edits. Blank strings stand for "no value"; `langId` is an id or null. */
export interface ProfileValues {
  email: string;
  username: string;
  name: string;
  surname: string;
  mobile: string;
  langId: string | null;
  /** Typed only while the email is being changed; never prefilled. */
  currentPassword: string;
}

export function profileValuesFromAccount(
  account: ProfileAccountSource | null | undefined,
): ProfileValues {
  return {
    email: account?.email ?? '',
    username: account?.username ?? '',
    name: account?.user?.name ?? '',
    surname: account?.user?.surname ?? '',
    mobile: account?.user?.mobile ?? '',
    langId: account?.user?.langId ?? null,
    currentPassword: '',
  };
}

// ─── Payloads ───────────────────────────────────────────────────────────────

export interface UpdateMeProfilePayload {
  email?: string;
  /** Present only with `email`: the server demands it for that change. */
  currentPassword?: string;
  username?: string;
  user?: { name: string; surname: string | null; mobile: string | null; langId: string | null };
}

export interface UpdateMeAvatarPayload {
  user: { avatar: string | null };
}

/** Whether `email` (trimmed) differs from what the server stores. */
export const isEmailChange = (email: string, stored: string): boolean =>
  email.trim().toLowerCase() !== stored.toLowerCase();

const blankToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/**
 * The write behind "Save": personal fields only, never an id (the server acts on the token's
 * account). The photo is not part of it; it is saved on its own (`buildAvatarPayload`). Email and
 * username travel only when they differ from the `original` account values, lower-cased like the
 * administration form does: login compares the username exactly, so re-sending an untouched
 * mixed-case value in lower case would lock the user out. An account without a user (a service
 * account) has no personal block to write.
 */
export function buildProfilePayload(
  values: ProfileValues,
  hasUser: boolean,
  original: ProfileValues,
): UpdateMeProfilePayload {
  const account: UpdateMeProfilePayload = {};
  if (isEmailChange(values.email, original.email)) {
    account.email = values.email.trim().toLowerCase();
    account.currentPassword = values.currentPassword;
  }
  if (values.username.trim() !== original.username)
    account.username = values.username.trim().toLowerCase();
  if (!hasUser) return account;

  return {
    ...account,
    user: {
      name: values.name.trim(),
      surname: blankToNull(values.surname),
      mobile: blankToNull(values.mobile),
      langId: values.langId,
    },
  };
}

/** The write behind a photo change: a data URI to set it, `null` to remove it. */
export function buildAvatarPayload(avatar: string | null): UpdateMeAvatarPayload {
  return { user: { avatar } };
}

// ─── Dialog ─────────────────────────────────────────────────────────────────

/**
 * Id of the dialog description, passed as `ariaDescribedBy` when the dialog opens. Spartan's
 * description directive registers itself after the dialog container was first checked, which
 * throws NG0100 in dev mode; a fixed id known at open time keeps the container binding stable.
 */
export const ACCOUNT_PROFILE_DESCRIPTION_ID = 'iam-account-profile-description';

// ─── Password ───────────────────────────────────────────────────────────────

/** Same rule set as the account administration form (`account-form.component.ts`). */
export const PASSWORD_RULES: HlmPasswordStrengthRules = {
  minLength: 8,
  digit: true,
  specialCharacter: true,
  lowerCase: true,
  upperCase: true,
};
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 30;

const SPECIAL_CHAR_REGEX = /[!@#$%^&*()+_\-=}{[\]|:;"/?.><,`~]/;

export type PasswordRuleKind = 'digit' | 'specialCharacter' | 'lowerCase' | 'upperCase';

/** The character rules `password` breaks, in the order the strength indicator lists them. */
export function failedPasswordRules(password: string): PasswordRuleKind[] {
  const failed: PasswordRuleKind[] = [];
  if (!/[0-9]/.test(password)) failed.push('digit');
  if (!SPECIAL_CHAR_REGEX.test(password)) failed.push('specialCharacter');
  if (!/[a-z]/.test(password)) failed.push('lowerCase');
  if (!/[A-Z]/.test(password)) failed.push('upperCase');
  return failed;
}

export const passwordsMatch = (password: string, repeat: string): boolean => password === repeat;

// ─── Photo ──────────────────────────────────────────────────────────────────

/** Types the API accepts for a photo; SVG is refused on purpose (it can carry script). */
export const AVATAR_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;

export const isAllowedAvatarFile = (file: { type: string }): boolean =>
  (AVATAR_ALLOWED_TYPES as readonly string[]).includes(file.type);

/** `data:<type>;base64,<payload>` of the Blob's bytes, the shape `user.avatar` is stored in. */
export function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
